import { useState, useEffect, useCallback } from "react";
import { CheckCircle } from "@untitledui/icons";
import { getMyGarage } from "@/api/garage";
import type { GarageDto, SubscriptionTier } from "@/types/dto";
import { cx } from "@/utils/cx";

interface TierConfig {
  name: string;
  price: number | null; // null = contact sales
  description: string;
  features: string[];
  highlighted?: boolean;
}

const TIERS: Record<SubscriptionTier, TierConfig> = {
  free: {
    name: "Trial",
    price: 0,
    description: "14-day free trial — then pick a plan",
    features: [
      "1 user (owner only)",
      "50 customers & vehicles",
      "20 jobs / month",
      "15 invoices / month",
      "Daily Closing report",
    ],
  },
  starter: {
    name: "Starter",
    price: 9,
    description: "For solo mechanics and micro garages",
    highlighted: true,
    features: [
      "2 users",
      "150 customers & vehicles",
      "Unlimited jobs & invoices",
      "All reports + CSV export",
      "Appointment scheduling",
      "Owner & Mechanic roles",
      "Email support",
      "1 year data retention",
    ],
  },
  pro: {
    name: "Pro",
    price: 24,
    description: "For small garages with a growing team",
    features: [
      "5 users",
      "Unlimited customers & vehicles",
      "Unlimited jobs & invoices",
      "All reports + CSV + Print",
      "Appointment scheduling",
      "Inventory tracking",
      "25 storage units",
      "Owner, Manager, Mechanic & Receptionist",
      "Priority support (24h)",
      "2 years data retention",
    ],
  },
  enterprise: {
    name: "Business",
    price: 49,
    description: "For established garages that need it all",
    features: [
      "15 users",
      "Unlimited everything",
      "Full inventory management",
      "Unlimited storage units",
      "All 6 roles",
      "Custom logo",
      "Priority support (12h)",
      "Unlimited data retention",
    ],
  },
};

const TIER_ORDER: SubscriptionTier[] = ["starter", "pro", "enterprise"];

const ADDONS = [
  { name: "Extra Users", price: 3, unit: "/ user / mo", description: "Add team members beyond your plan limit" },
  { name: "SMS Reminders", price: 8, unit: "/ mo + €0.05/SMS", description: "Appointment reminders & job updates" },
  { name: "WhatsApp Notifications", price: 12, unit: "/ mo", description: "Job updates via WhatsApp Business" },
  { name: "Extra Storage Units", price: 1, unit: "/ unit / mo", description: "Expand beyond your plan limit" },
  { name: "Automated Email Reports", price: 10, unit: "/ mo", description: "Weekly/monthly reports to your inbox" },
  { name: "Custom Domain", price: 5, unit: "/ mo", description: "Access on your own subdomain" },
];

export default function BillingSettingsPage() {
  const [garage, setGarage] = useState<GarageDto | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchGarage = useCallback(async () => {
    try {
      const data = await getMyGarage();
      setGarage(data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGarage();
  }, [fetchGarage]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-md font-semibold text-text-primary">Billing</h2>
          <p className="mt-1 text-sm text-text-tertiary">Loading...</p>
        </div>
      </div>
    );
  }

  const rawTier = (garage?.subscriptionTier ?? "free") as SubscriptionTier;
  const currentTier = rawTier === "free" ? "free" : rawTier;
  const currentIndex = TIER_ORDER.indexOf(currentTier);
  const isTrial = rawTier === "free";

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-md font-semibold text-text-primary">Billing</h2>
        <p className="mt-1 text-sm text-text-tertiary">
          Manage your subscription plan and add-ons.
        </p>
      </div>

      {/* Current Plan */}
      <div className={cx(
        "rounded-xl border p-6",
        isTrial ? "border-yellow-300 bg-yellow-50" : "border-border-secondary bg-bg-primary"
      )}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <p className="text-sm text-text-tertiary">Current plan</p>
            <p className="text-lg font-semibold text-text-primary mt-0.5">
              {isTrial ? "Free Trial" : TIERS[currentTier]?.name ?? "Unknown"}
              {!isTrial && TIERS[currentTier] && (
                <span className="ml-2 text-sm font-normal text-text-tertiary">
                  €{TIERS[currentTier].price}/mo
                </span>
              )}
            </p>
            {isTrial && (
              <p className="text-sm text-yellow-700 mt-1">
                Pick a plan below to continue using My Garage after your trial ends.
              </p>
            )}
          </div>
          <span className={cx(
            "inline-flex items-center rounded-full px-3 py-1 text-xs font-medium w-fit",
            isTrial ? "bg-yellow-100 text-yellow-700" : "bg-green-100 text-green-700"
          )}>
            {isTrial ? "Trial" : "Active"}
          </span>
        </div>
      </div>

      {/* Plans Grid */}
      <div>
        <h3 className="text-sm font-semibold text-text-primary mb-4">Available plans</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
          {TIER_ORDER.map((tier, index) => {
            const config = TIERS[tier];
            const isCurrent = !isTrial && tier === currentTier;
            const isUpgrade = isTrial || index > currentIndex;

            return (
              <div
                key={tier}
                className={cx(
                  "rounded-xl border p-5 flex flex-col",
                  config.highlighted && !isCurrent
                    ? "border-fg-brand-primary ring-1 ring-fg-brand-primary"
                    : "border-border-secondary",
                  isCurrent && "bg-bg-secondary"
                )}
              >
                {config.highlighted && (
                  <span className="inline-flex self-start items-center rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-fg-brand-primary mb-3">
                    Most popular
                  </span>
                )}
                <h4 className="text-sm font-semibold text-text-primary">{config.name}</h4>
                <div className="mt-2 flex items-baseline gap-1">
                  <span className="text-2xl font-bold text-text-primary">
                    €{config.price}
                  </span>
                  <span className="text-sm text-text-tertiary">/ mo</span>
                </div>
                <p className="mt-1 text-xs text-text-tertiary">{config.description}</p>

                <ul className="mt-4 space-y-2 flex-1">
                  {config.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-text-secondary">
                      <CheckCircle className="size-4 shrink-0 text-fg-brand-primary mt-0.5" />
                      {feature}
                    </li>
                  ))}
                </ul>

                <button
                  disabled={isCurrent}
                  className={cx(
                    "mt-5 w-full rounded-lg px-4 py-2 text-sm font-medium transition-colors",
                    isCurrent
                      ? "bg-bg-tertiary text-text-tertiary cursor-default"
                      : isUpgrade
                        ? "bg-fg-brand-primary text-white hover:bg-fg-brand-primary_hover"
                        : "border border-border-secondary text-text-secondary hover:bg-bg-secondary_hover"
                  )}
                >
                  {isCurrent ? "Current plan" : isUpgrade ? "Upgrade" : "Downgrade"}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Add-ons */}
      <div>
        <h3 className="text-sm font-semibold text-text-primary mb-1">Add-ons</h3>
        <p className="text-xs text-text-tertiary mb-4">Available on Starter and Pro plans</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {ADDONS.map((addon) => (
            <div
              key={addon.name}
              className="rounded-xl border border-border-secondary bg-bg-primary p-4 flex flex-col gap-2"
            >
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-text-primary">{addon.name}</p>
                  <p className="text-xs text-text-tertiary mt-0.5">{addon.description}</p>
                </div>
                <button
                  disabled={currentTier === "free"}
                  className={cx(
                    "rounded-lg border border-border-secondary px-3 py-1.5 text-xs font-medium transition-colors shrink-0",
                    currentTier === "free"
                      ? "text-text-disabled cursor-not-allowed"
                      : "text-text-secondary hover:bg-bg-secondary_hover"
                  )}
                >
                  Add
                </button>
              </div>
              <p className="text-sm font-semibold text-text-primary">
                €{addon.price} <span className="text-xs font-normal text-text-tertiary">{addon.unit}</span>
              </p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
